import { CHART, SERIES } from '@/lib/chartColors';
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart, Area, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import { apiClient } from '@/lib/api';
import { formatCompact, formatNumber } from '@/lib/utils';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Segmented } from '@/components/ui/Segmented';
import { StatTile } from '@/components/ui/StatTile';
import { ChartTooltip } from '@/components/charts/ChartTooltip';

type Period = 7 | 30 | 90;

interface OverviewRow { date: string; requests: number; credits: number; errors: number; p95: number }
interface ServiceRow { service: string; requests: number; errors: number; p50: number; p95: number; credits: number }
interface StatusRow { status_code: number; requests: number }
interface TileRow { tileset: string; z: number; requests: number }
interface TopUserRow { user_id: string; total_credits: number; requests: number }
interface Acquisition {
  days: { date: string; maps: number; console: number; total: number }[];
  totalBefore: number; totalNow: number; newInRange: number;
}

// One accent and its neutrals, so every chart reads as the same system.
const TILESET_COLORS: Record<string, string> = { africa: CHART.primary, world: CHART.neutral, coast: SERIES[1]! };
const ERRORS = SERIES[4]!; // deep terracotta: errors stand out without a second hue
const PERIODS = [{ value: 7 as Period, label: '7 days' }, { value: 30 as Period, label: '30 days' }, { value: 90 as Period, label: '90 days' }];
const naira = (credits: number) => new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(credits / 10);
const shortDate = (iso: unknown) => typeof iso === 'string' && /^\d{4}-\d{2}-\d{2}/.test(iso)
  ? new Date(`${iso.slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  : String(iso);
const axis = { tickLine: false, axisLine: false } as const;

/** ClickHouse may send counts as text ("1447"); charts need numbers. Dates and names stay text. */
export function toNumbers<T>(rows: Record<string, unknown>[]): T[] {
  return rows.map(r => Object.fromEntries(Object.entries(r).map(([k, v]) =>
    [k, typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) ? Number(v) : v]))) as T[];
}

/** Admin-only platform aggregates from ClickHouse (analytics-api). */
const fetchAgg = <T,>(path: string, days?: number) =>
  apiClient.get(`/analytics/${path}`, days ? { params: { days } } : {}).then(r => toNumbers<T>(r.data.data ?? []));

const Empty: React.FC<{ loading: boolean; height?: string }> = ({ loading, height = 'h-64' }) =>
  loading ? <div className={`skeleton ${height}`} aria-hidden="true" /> : <p className="py-10 text-center text-sm text-muted-foreground">No data for this period.</p>;
const th = 'px-5 py-2.5 text-left text-xs font-medium text-muted-foreground';
const td = 'px-5 py-3 font-mono text-[13px] tabular-nums text-foreground';

const PlatformAnalyticsPage: React.FC = () => {
  const [period, setPeriod] = useState<Period>(30);

  const overview = useQuery<OverviewRow[]>({ queryKey: ['ch-overview', period], queryFn: () => fetchAgg('overview', period) });
  const byService = useQuery<ServiceRow[]>({ queryKey: ['ch-by-service', period], queryFn: () => fetchAgg('by-service', period) });
  const statusCodes = useQuery<StatusRow[]>({ queryKey: ['ch-status-codes', period], queryFn: () => fetchAgg('status-codes', period) });
  const tiles = useQuery<TileRow[]>({ queryKey: ['ch-tiles', period], queryFn: () => fetchAgg('tiles', period) });
  const topUsers = useQuery<TopUserRow[]>({ queryKey: ['ch-top-users'], queryFn: () => fetchAgg('top-users') });
  // Sign-ups come from the gateway's accounts database, not ClickHouse.
  const acquisition = useQuery<Acquisition>({
    queryKey: ['acquisition', period],
    queryFn: () => apiClient.get('/admin/users/acquisition', { params: { days: period } }).then(r => r.data),
  });
  const acqSplit = (acquisition.data?.days ?? []).reduce((t, d) => ({ maps: t.maps + d.maps, console: t.console + d.console }), { maps: 0, console: 0 });

  const anyError = overview.error || byService.error || statusCodes.error || tiles.error || topUsers.error || acquisition.error;
  const totals = (overview.data ?? []).reduce(
    (t, r) => ({ requests: t.requests + Number(r.requests), errors: t.errors + Number(r.errors), credits: t.credits + Number(r.credits) }),
    { requests: 0, errors: 0, credits: 0 },
  );
  // Tiles by zoom, one bar per tileset.
  const tilesets = [...new Set((tiles.data ?? []).map(t => t.tileset))];
  const tilesByZoom = Object.values((tiles.data ?? []).reduce<Record<number, Record<string, number>>>((acc, t) => {
    acc[t.z] ??= { z: t.z };
    acc[t.z]![t.tileset] = Number(t.requests);
    return acc;
  }, {})).sort((a, b) => a.z! - b.z!);

  const errorRate = totals.requests ? (totals.errors / totals.requests) * 100 : 0;
  const days = overview.data ?? [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Platform analytics"
        description="Requests, errors, latency, map tiles and sign-ups across every account."
        actions={<Segmented label="Period" value={period} options={PERIODS} onChange={setPeriod} />}
      />

      {!!anyError && (
        <div role="alert" className="rounded-2xl border border-error/30 bg-error/10 px-4 py-3 text-sm text-foreground">
          Could not load analytics. The analytics service or ClickHouse may be unreachable; check Platform health.
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatTile label="Requests" value={formatNumber(totals.requests)} hint={`Last ${period} days`} trend={days.map(d => Number(d.requests))} loading={overview.isLoading} />
        <StatTile label="Errors (4xx and 5xx)" value={formatNumber(totals.errors)} hint={`${errorRate.toFixed(1)}% of requests`} trend={days.map(d => Number(d.errors))} loading={overview.isLoading} />
        <StatTile label="Credits used" value={formatNumber(totals.credits)} hint={naira(totals.credits)} trend={days.map(d => Number(d.credits))} loading={overview.isLoading} />
        <StatTile
          label="New sign-ups"
          value={formatNumber(acquisition.data?.newInRange ?? 0)}
          hint={acquisition.data ? `${formatNumber(acquisition.data.totalNow)} accounts` : undefined}
          trend={(acquisition.data?.days ?? []).map(d => d.total)}
          loading={acquisition.isLoading}
        />
      </div>

      <Panel title="Requests and errors per day">
        {days.length ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={days} margin={{ top: 8, right: 4, bottom: 0, left: -8 }}>
                <defs>
                  <linearGradient id="paReq" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={CHART.primary} stopOpacity={0.28} /><stop offset="100%" stopColor={CHART.primary} stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid vertical={false} strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axis} />
                <YAxis tickFormatter={formatCompact} width={48} {...axis} />
                <Tooltip content={<ChartTooltip />} labelFormatter={shortDate} cursor={{ stroke: 'rgb(var(--color-border))' }} />
                <Legend iconType="circle" iconSize={8} />
                <Area type="monotone" dataKey="requests" name="Requests" stroke={CHART.primary} strokeWidth={2} fill="url(#paReq)" />
                <Area type="monotone" dataKey="errors" name="Errors" stroke={ERRORS} strokeWidth={1.5} fill={ERRORS} fillOpacity={0.12} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        ) : <Empty loading={overview.isLoading} height="h-80" />}
      </Panel>

      <div className="grid gap-4 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <Panel title="User acquisition">
          {acquisition.data ? (
            <>
              <p className="mb-4 text-sm text-muted-foreground">
                <span className="text-foreground">{formatNumber(acquisition.data.newInRange)} new sign-ups</span> in the last {period} days
                {' '}(Maps app {formatNumber(acqSplit.maps)}, Developer console {formatNumber(acqSplit.console)}); {formatNumber(acquisition.data.totalNow)} accounts in total.
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={acquisition.data.days} margin={{ top: 8, right: 0, bottom: 0, left: -16 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={24} {...axis} />
                    <YAxis yAxisId="new" allowDecimals={false} tickFormatter={formatCompact} width={36} {...axis} />
                    <YAxis yAxisId="total" orientation="right" allowDecimals={false} tickFormatter={formatCompact} width={44} {...axis} />
                    <Tooltip content={<ChartTooltip />} labelFormatter={shortDate} cursor={{ fill: 'rgb(var(--color-surface-raised))' }} />
                    <Legend iconType="circle" iconSize={8} />
                    <Bar yAxisId="new" dataKey="maps" name="Maps app" stackId="s" fill={CHART.primary} radius={[0, 0, 0, 0]} />
                    <Bar yAxisId="new" dataKey="console" name="Developer console" stackId="s" fill={SERIES[1]} radius={[3, 3, 0, 0]} />
                    <Line yAxisId="total" type="monotone" dataKey="total" name="Total accounts" stroke={CHART.neutral} strokeWidth={1.5} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </>
          ) : <Empty loading={acquisition.isLoading} />}
        </Panel>

        <Panel title="Status codes">
          {statusCodes.data?.length ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={statusCodes.data.map(r => ({ code: String(r.status_code), requests: Number(r.requests) }))} margin={{ top: 8, right: 0, bottom: 0, left: -8 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="code" {...axis} />
                  <YAxis tickFormatter={formatCompact} width={48} {...axis} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: 'rgb(var(--color-surface-raised))' }} />
                  <Bar dataKey="requests" name="Requests" fill={CHART.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : <Empty loading={statusCodes.isLoading} height="h-72" />}
        </Panel>
      </div>

      <Panel title="By service" bodyClassName="px-0 pb-2">
        {byService.data?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px]">
              <thead className="border-b border-border">
                <tr><th className={th}>Service</th><th className={`${th} text-right`}>Requests</th><th className={`${th} text-right`}>Errors</th><th className={`${th} text-right`}>p50</th><th className={`${th} text-right`}>p95</th><th className={`${th} text-right`}>Credits</th></tr>
              </thead>
              <tbody className="divide-y divide-border">
                {byService.data.map(r => (
                  <tr key={r.service} className="hover:bg-surface-raised/60">
                    <td className="px-5 py-3 text-sm text-foreground">{r.service}</td>
                    <td className={`${td} text-right`}>{formatNumber(Number(r.requests))}</td>
                    <td className={`${td} text-right`}>{formatNumber(Number(r.errors))}</td>
                    <td className={`${td} text-right text-muted-foreground`}>{Math.round(Number(r.p50))} ms</td>
                    <td className={`${td} text-right text-muted-foreground`}>{Math.round(Number(r.p95))} ms</td>
                    <td className={`${td} text-right`}>{formatNumber(Number(r.credits))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <div className="px-5 pb-3"><Empty loading={byService.isLoading} height="h-40" /></div>}
      </Panel>

      <div className="grid gap-4 xl:grid-cols-2">
        <Panel title="Tile requests by zoom">
          {tilesByZoom.length ? (
            <>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={tilesByZoom} margin={{ top: 8, right: 0, bottom: 0, left: -8 }}>
                    <CartesianGrid vertical={false} strokeDasharray="3 3" />
                    <XAxis dataKey="z" {...axis} />
                    <YAxis tickFormatter={formatCompact} width={48} {...axis} />
                    <Tooltip content={<ChartTooltip />} labelFormatter={z => `Zoom ${z}`} cursor={{ fill: 'rgb(var(--color-surface-raised))' }} />
                    {tilesets.map(t => <Bar key={t} dataKey={t} stackId="t" name={t} fill={TILESET_COLORS[t] ?? CHART.neutral} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <ul className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
                {tilesets.map(t => (
                  <li key={t} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ background: TILESET_COLORS[t] ?? CHART.neutral }} aria-hidden="true" />
                    <span className="text-foreground">{t}</span>
                    <span className="font-mono tabular-nums">{formatNumber((tiles.data ?? []).filter(x => x.tileset === t).reduce((n, x) => n + Number(x.requests), 0))}</span>
                  </li>
                ))}
              </ul>
            </>
          ) : <Empty loading={tiles.isLoading} />}
        </Panel>

        <Panel title="Top accounts by credits, last 30 days" bodyClassName="px-0 pb-2">
          {topUsers.data?.length ? (
            <table className="w-full">
              <thead className="border-b border-border"><tr><th className={th}>Account</th><th className={`${th} text-right`}>Credits</th><th className={`${th} text-right`}>Requests</th></tr></thead>
              <tbody className="divide-y divide-border">
                {topUsers.data.map(u => (
                  <tr key={u.user_id} className="hover:bg-surface-raised/60">
                    <td className="max-w-[16ch] truncate px-5 py-3 font-mono text-xs text-foreground" title={u.user_id}>{u.user_id}</td>
                    <td className={`${td} text-right`}>{formatNumber(Number(u.total_credits))}</td>
                    <td className={`${td} text-right`}>{formatNumber(Number(u.requests))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="px-5 pb-3"><Empty loading={topUsers.isLoading} height="h-40" /></div>}
        </Panel>
      </div>
    </div>
  );
};

export default PlatformAnalyticsPage;
